import { computed } from 'vue';
import { useSearchAdvicesStore } from '~/composables/_next/stores/useSearchAdvicesStore';
import { useSearchRecipesStore } from '~/composables/_next/stores/useSearchRecipesStore';
import { useSearchProductsStore } from '~/composables/_next/stores/useSearchProductsStore';

import type { Facet, Pagination, ProductSorting } from '~/types/_next/search';
type SearchFacets = {
  advices: Facet[];
  recipes: Facet[];
  products: Facet[];
};
type SearchSorting = {
  advices: ProductSorting;
  recipes: ProductSorting;
  products: ProductSorting;
};

export function useSearchStore() {
  const { searchAdvices, advices, adviceFacets, advicePagination, adviceSorting } = useSearchAdvicesStore();
  const { searchProducts, products, productFacets, productPagination, productSorting } = useSearchProductsStore();
  const { searchRecipes, recipes, recipeFacets, recipePagination, recipeSorting } = useSearchRecipesStore();

  async function search(type: string, forcedSort?: string) {
    switch (type) {
      case 'advices':
        return await searchAdvices(null, forcedSort);
      case 'recipes':
        return await searchRecipes(null, forcedSort);
      default:
        return await searchProducts(forcedSort);
    }
  }

  async function fetchAllSearchResults() {
    await Promise.all([searchProducts(), searchAdvices(), searchRecipes()]);
  }

  const searchResults = computed(() => ({
    advices: advices.value ?? [],
    recipes: recipes.value ?? [],
    products: products.value ?? [],
  }));

  const searchFacets = computed<SearchFacets>(() => ({
    advices: adviceFacets.value ?? [],
    recipes: recipeFacets.value ?? [],
    products: productFacets.value ?? [],
  }));

  const pagination = computed(() => ({
    advices: advicePagination.value ?? ({} as Pagination),
    recipes: recipePagination.value ?? ({} as Pagination),
    products: productPagination.value ?? ({} as Pagination),
  }));

  const sorting = computed<SearchSorting>(() => ({
    advices: adviceSorting.value ?? ({} as ProductSorting),
    recipes: recipeSorting.value ?? ({} as ProductSorting),
    products: productSorting.value ?? ({} as ProductSorting),
  }));

  return {
    fetchAllSearchResults,
    search,
    searchResults,
    pagination,
    sorting,
    searchFacets,
  };
}
